import { EASTER_EGGS } from "../shared/constants";

const FEATURE_DISABLED = (stationId?: string | null): boolean =>
  !!stationId;

/**
 * Returns the first easter-egg phrase still present in `text`, or null.
 * Treats the whole text as case-insensitive. The CO Review approve flow uses this
 * to gate Approve when the officer hasn't removed the inserted phrase yet.
 *
 * Per-station disable list at shared/constants.ts:106 keeps the existing officer-side
 * exemption working.
 */
export function findEasterEggPhrase(
  text: string | null | undefined,
  stationId?: string | null,
): string | null {
  if (!text) return null;
  if (FEATURE_DISABLED(stationId)) return null;
  const haystack = text.toLowerCase();
  for (const phrase of EASTER_EGGS) {
    if (haystack.includes(phrase.toLowerCase())) {
      return phrase;
    }
  }
  return null;
}

export function containsAnyEasterEgg(
  text: string | null | undefined,
  stationId?: string | null,
): boolean {
  return findEasterEggPhrase(text, stationId) !== null;
}

/**
 * Walk every narrative entry in a report's formData and return the first egg found,
 * or null if all narratives are clean.
 */
export function findEasterEggInNarratives(
  formData: Record<string, unknown> | null | undefined,
  stationId?: string | null,
): { narrativeIndex: number; phrase: string } | null {
  if (!formData) return null;
  const narratives = formData["Narratives"];
  if (!Array.isArray(narratives)) return null;
  for (let i = 0; i < narratives.length; i++) {
    const entry = narratives[i] as Record<string, unknown> | null;
    if (!entry) continue;
    // eForce uses `Text`; generic uses `Narrative`. Try both.
    const candidate =
      typeof entry.Text === "string"
        ? entry.Text
        : typeof entry.Narrative === "string"
          ? entry.Narrative
          : null;
    const phrase = findEasterEggPhrase(candidate, stationId);
    if (phrase) {
      return { narrativeIndex: i, phrase };
    }
  }
  return null;
}
